import React, { useEffect, useState } from 'react'
import { CaretLeft, Plus, Trash } from '@phosphor-icons/react'
import { Button, TimePicker } from 'antd'
import SelectDatesCalendar from 'components/global/SelectDatesCalendar'
import moment, { Moment } from 'moment'
import { SpecialSchedule } from 'types/globalTypes'
import {
  combineDateAndTimeToUtcString,
  convertMinutesToLocalTime,
  convertTimeToUtcMinutes,
} from 'helper/utility'
import SpecialScheduleModal from '../../../CreateEvent/Screens/GeneralDetails/GeneralDetailModal/AddSpecialScheduleModal'

import { classesEventStore } from 'state/classes/classesStore'
import { useEditClassStep3Mutation } from 'api/Classes/Classes' 
import ButtonSpinner from 'components/global/ButtonSpinner'
import { infoMessage } from 'api/global'

interface DaysAndHoursProps {
  handleNext: (data: any) => void
  handleBack: () => void
}

const DaysAndHours: React.FC<DaysAndHoursProps> = ({ handleNext, handleBack }) => {
  const { classesData, setClassesData } = classesEventStore()
  
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [defaultTime, setDefaultTime] = useState({
    startTime: moment('9:00', 'HH:mm'),
    endTime: moment('15:00', 'HH:mm'),
  })
  const [specialScheduleData, setSpecialScheduleData] = useState<SpecialSchedule[]>([])
  const [addSpecialScheduleModalVisible, setAddSpecialScheduleModalVisible] =
    useState<boolean>(false)


  const { mutate: editClassStep3, isLoading: editLoading } = useEditClassStep3Mutation()

  const handleStartTimeChange = (time: Moment | null) => {
    if (time) setDefaultTime({ ...defaultTime, startTime: time })
  }
  const handleEndTimeChange = (time: Moment | null) => {
    if (time) setDefaultTime({ ...defaultTime, endTime: time })
  }

  const removeSpecialscheduleInput = (index: number) => {
    const newData = [...specialScheduleData];
    newData.splice(index, 1);
    setSpecialScheduleData(newData);
  }

  const handleSubmit = () => {
    if (selectedDates.length === 0) {
      infoMessage('Please select at least one date')
      return
    }

    const payload = {
      datesOfEvent: selectedDates.map((item: string) =>
        combineDateAndTimeToUtcString(
          item,
          defaultTime.startTime.format('hh:mm A'),
        ),
      ),
      timings: {
        startTime: convertTimeToUtcMinutes(defaultTime.startTime as any),
        endTime: convertTimeToUtcMinutes(defaultTime.endTime as any),
        specialSchedules: specialScheduleData.map((data: SpecialSchedule) => ({
          ...data,
          day: moment(data.day).format('YYYY-MM-DDTHH:mm:ss[Z]'),
        })),
      },
    }

    console.log("dataa",classesData)
    editClassStep3(
      { classId: classesData?._id, body: payload },
      {
        onSuccess: (data) => {
          setClassesData({ ...classesData, ...data })
          handleNext(data)
        },
      },
    )
  }

  // Sync initial data from Store
  useEffect(() => {
    if (classesData?.timings) {
      setSelectedDates(
        (classesData.datesOfEvent || []).map(d => moment(d).format('YYYY-MM-DD')),
      )
      setDefaultTime({
        startTime: moment(
          convertMinutesToLocalTime(classesData?.timings.startTime, 'HH:mm'),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime(classesData?.timings.endTime, 'HH:mm'),
          'HH:mm',
        ),
      })
      setSpecialScheduleData(classesData.timings?.specialSchedules || [])
    }
  }, [classesData?.timings])

  return (
    <div className="step3-main-container">
      <div className="content scroller overflow-auto-y">
        <h3 className="text-24 w-600"> Step 3. Days and Hours</h3>
        <p className="mt-20px">
          Select the dates and set the schedule for your class
        </p>
        
        <div className="calendar-width">
          <SelectDatesCalendar
            isLimited={false}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            showSelectDatesButtons={true}
          />
        </div>

        <div className="schedule-width mt-20px">
          <div className="default-schedule">
            <h2 className="text-14 w-600 mb-10px">Default schedule</h2>
            <div className="flex align-center gap-16px w-full max-w-[400px]">
              <TimePicker
                className="w-full form-control-field"
                placeholder="Start Time"
                format="h:mm a"
                value={defaultTime.startTime}
                showNow={false}
                clearIcon={false}
                onChange={handleStartTimeChange}
              />
              <div className="text-gray-400">-</div>
              <TimePicker
                className="w-full form-control-field"
                placeholder="End Time"
                format="h:mm a"
                value={defaultTime.endTime}
                showNow={false}
                clearIcon={false}
                onChange={handleEndTimeChange}
              />
            </div>
          </div>

          <div className="special-schedule mt-24px flex justify-between align-center">
            <h2 className="text-14 w-600">Special schedule</h2>
            <Plus
              className="pointer hover:bg-gray-100 rounded-full p-1"
              size={24}
              color="#000"
              onClick={() => setAddSpecialScheduleModalVisible(true)}
            />
          </div>

          {specialScheduleData.length > 0 && (
            <div className="mt-15px">
              {specialScheduleData.map((specialSchedule: SpecialSchedule, index: number) => (
                <div className="special-schedule-list flex justify-between align-center mb-12px p-12px border-gray-100 border rounded-lg" key={index}>
                  <div>
                      <h4 className="text-14 w-600">
                      {moment(specialSchedule.day).format('DD-MM-YYYY')}
                      </h4>
                      <div className="text-12 text-gray-500">
                          {convertMinutesToLocalTime(specialSchedule.startTime, 'h:mm a')} - {convertMinutesToLocalTime(specialSchedule.endTime, 'h:mm a')}
                      </div>
                  </div>

                  <Trash
                      size={20}
                      color={'#FF6961'}
                      className="pointer hover:opacity-70"
                      onClick={() => removeSpecialscheduleInput(index)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-15px event-button-cont">
        <h5
          className="flex gap-10px align-center pointer"
          onClick={() => handleBack()}
        >
          <CaretLeft size={20} color="#000" />
          <span className="text-15 w-500">Back</span>
        </h5>
        <Button
          className="stepper-button"
          disabled={editLoading}
          onClick={handleSubmit}
        >
          {editLoading ? (
            <ButtonSpinner size={20} color="#000" />
          ) : (
            'Next'
          )}
        </Button>
      </div>

      {addSpecialScheduleModalVisible && (
        <SpecialScheduleModal
          addSpecialScheduleModalVisible={addSpecialScheduleModalVisible}
          setAddSpecialScheduleModalVisible={setAddSpecialScheduleModalVisible}
          selectedDates={selectedDates}
          setSpecialScheduleData={setSpecialScheduleData}
          specialScheduleData={specialScheduleData}
        />
      )}
    </div>
  )
}

export default DaysAndHours